// Walking the solution tree (spec §12.1) for display: every line from the
// start, the main line first.
import type { LegalMove } from "../api/client";
import { apply } from "./textfen";

export type WhiteNode = Record<string, { main?: boolean; end?: boolean; b?: { move: string; w?: WhiteNode }[] }>;
export type Line = { moves: string[]; main: boolean; diverges: number };

type BlackNode = { move: string; w?: WhiteNode }[];

/** A tree starts with white (`w`), or with black's introduction (`b`). */
export function lines(solution: { w?: WhiteNode; b?: BlackNode }): Line[] {
  const out: { moves: string[]; main: boolean }[] = [];
  const walk = (w: WhiteNode, prefix: string[], isMain: boolean) => {
    Object.entries(w).forEach(([name, entry], i) => {
      const p = [...prefix, name];
      const main = isMain && i === 0;
      if (!entry.b?.length) {
        out.push({ moves: p, main });
        return;
      }
      entry.b.forEach((d, j) => {
        const q = [...p, d.move];
        if (!d.w) out.push({ moves: q, main: main && j === 0 });
        else walk(d.w, q, main && j === 0);
      });
    });
  };
  if (solution.b) {
    solution.b.forEach((d, j) => {
      if (d.w) walk(d.w, [d.move], j === 0);
      else out.push({ moves: [d.move], main: j === 0 });
    });
  } else if (solution.w) walk(solution.w, [], true);
  const mainLine = out.find((l) => l.main)?.moves ?? [];
  return out
    .sort((a, b) => Number(b.main) - Number(a.main))
    .map((l) => {
      let k = 0;
      while (k < l.moves.length && l.moves[k] === mainLine[k]) k++;
      return { ...l, diverges: l.main ? 0 : k };
    });
}

/** The positions along a line, and each move's details. */
export function walkLine(
  textfen: string,
  moves: string[],
  positions: Record<string, LegalMove[]>,
): { fens: string[]; details: LegalMove[] } {
  const fens = [textfen];
  const details: LegalMove[] = [];
  let fen = textfen;
  for (const name of moves) {
    const m = (positions[fen] ?? []).find((x) => x.move === name);
    if (!m) break;
    details.push(m);
    fen = apply(fen, m);
    fens.push(fen);
  }
  return { fens, details };
}

/** "3." before white's moves, "3..." before a black move that opens a row. */
export function moveNumber(ply: number, first: boolean): string {
  const n = Math.floor(ply / 2) + 1;
  if (ply % 2 === 0) return `${n}.`;
  return first ? `${n}...` : "";
}
